import { useEffect, useState } from "react";
import { ThumbsUp, ThumbsDown, Check } from "lucide-react";
import { submitFeatureFeedback } from "@/lib/testimonials";

export function FeatureFeedback({ feature }: { feature: string }) {
  const key = `rt_feature_fb_done_${feature}`;
  const [helpful, setHelpful] = useState<boolean | null>(null);
  const [comment, setComment] = useState("");
  const [done, setDone] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined" && localStorage.getItem(key)) {
      setDone(true);
    }
  }, [key]);

  const pick = (value: boolean) => {
    setHelpful(value);
    setOpen(true);
  };

  const submit = () => {
    if (helpful === null) return;
    submitFeatureFeedback({ feature, helpful, comment: comment || undefined });
    if (typeof window !== "undefined") localStorage.setItem(key, "1");
    setDone(true);
  };

  if (done) {
    return (
      <div className="mt-6 flex items-center justify-center gap-2 rounded-2xl border border-gold/30 bg-cream/40 p-4 text-sm text-muted-foreground">
        <Check className="h-4 w-4 text-[var(--gold-deep)]" />
        Thanks for your feedback on {feature}.
      </div>
    );
  }

  return (
    <section className="mt-6 rounded-2xl border border-gold/30 bg-card p-5 text-center shadow-sm">
      <p className="text-sm font-medium text-foreground">
        Was {feature} helpful for you today?
      </p>
      <div className="mt-3 flex items-center justify-center gap-3">
        <button
          type="button"
          onClick={() => pick(true)}
          className={`inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border px-4 py-2 text-sm transition ${
            helpful === true
              ? "border-gold bg-gold/15 text-foreground"
              : "border-border/60 text-foreground hover:bg-gold/10"
          }`}
        >
          <ThumbsUp className="h-4 w-4" /> Helpful
        </button>
        <button
          type="button"
          onClick={() => pick(false)}
          className={`inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border px-4 py-2 text-sm transition ${
            helpful === false
              ? "border-gold bg-gold/15 text-foreground"
              : "border-border/60 text-foreground hover:bg-gold/10"
          }`}
        >
          <ThumbsDown className="h-4 w-4" /> Needs Improvement
        </button>
      </div>
      {open && (
        <div className="mx-auto mt-4 max-w-lg text-start">
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={2}
            maxLength={600}
            placeholder="Optional: tell us more (what helped, what could improve)…"
            className="w-full rounded-xl border border-border/60 bg-background px-3 py-2 text-sm outline-none focus:border-gold/50"
          />
          <div className="mt-2 flex justify-end">
            <button type="button" onClick={submit} className="btn-gold text-sm">
              Send feedback
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
